import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { ClaimCard } from '@/components/ui/shared/ClaimCard';

/**
 * Claim - Claims overview widget with modern UI
 * Shows Total Claims, Reversed Claims, Net Claims, Paid Claims
 * Optionally shows Reversal Rate and Compensability Rate for specific users
 */
function Claim({ data, _Index }) {
    // State
    const [claimsData, setClaimsData] = useState({
        totalClaims: '-',
        reversedClaims: '-',
        netClaims: '-',
        paidClaims: '-',
    });
    const [isLoading, setIsLoading] = useState(!!(data?.RequestJson && data?.url));
    const [error, setError] = useState(null);

    // Refs
    const abortControllerRef = useRef(null);
    const prevRequestJsonRef = useRef(null);

    // Get user data
    const userdata = useMemo(() => {
        try {
            return JSON.parse(localStorage.getItem('user')) || {};
        } catch {
            return {};
        }
    }, []);

    // Calculate rates
    const rates = useMemo(() => {
        try {
            const pd = parseFloat(claimsData.totalClaims.replace(/,/g, ''));
            const rev = parseFloat(claimsData.reversedClaims.replace(/,/g, ''));
            const nc = parseFloat(claimsData.netClaims.replace(/,/g, ''));
            const cc = parseFloat(claimsData.paidClaims.replace(/,/g, ''));

            const reversalRate = pd > 0 ? ((rev / pd) * 100).toFixed(2) : '0.00';
            const compensabilityRate = nc > 0 ? ((1 - (cc / nc)) * 100).toFixed(2) : '0.00';

            return { reversalRate, compensabilityRate };
        } catch {
            return { reversalRate: '0.00', compensabilityRate: '0.00' };
        }
    }, [claimsData]);

    // Check if should show rates section
    const showRates = (userdata.wholesaler_Id === 255 || userdata.wholesaler_Id === 294) && userdata.userrole === 'Wholesaler';

    // Handle API response
    const handleResponse = useCallback((response) => {
        return response.text().then(text => {
            try {
                return text && JSON.parse(text);
            } catch {
                return [];
            }
        });
    }, []);

    // Fetch data
    const fetchData = useCallback(async () => {
        if (!data?.RequestJson || !data?.url) return;

        // Cancel any previous pending request
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }

        const controller = new AbortController();
        abortControllerRef.current = controller;

        setIsLoading(true);
        setError(null);

        try {
            const response = await fetch(data.url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'requestHeader': data.RequestJson1,
                    'X-Browser-Url': window.location.href,
                },
                body: data.RequestJson,
                signal: controller.signal,
            });

            const apidata = await handleResponse(response);

            if (controller.signal.aborted) return;

            if (apidata?.[0]?.legend?.length >= 4) {
                setClaimsData({
                    totalClaims: apidata[0].legend[0].legendValue,
                    reversedClaims: apidata[0].legend[1].legendValue,
                    netClaims: apidata[0].legend[2].legendValue,
                    paidClaims: apidata[0].legend[3].legendValue,
                });
            }

            setIsLoading(false);
        } catch (err) {
            if (err.name === 'AbortError') return;
            setError('Error loading data');
            setIsLoading(false);
        }
    }, [data?.url, data?.RequestJson, data?.RequestJson1, handleResponse]);

    // Fetch data when RequestJson changes
    useEffect(() => {
        if (data?.type === 'Claim' && data?.RequestJson && data?.url &&
            data.RequestJson !== prevRequestJsonRef.current) {
            prevRequestJsonRef.current = data.RequestJson;
            fetchData();
        }
    }, [data?.type, data?.RequestJson, data?.url, fetchData]);

    // Initial fetch
    useEffect(() => {
        if (data?.type === 'Claim' && data?.RequestJson && data?.url) {
            fetchData();
        }

        return () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, [data?.type, data?.RequestJson, data?.url, fetchData]);

    // Claims items configuration
    const claimsItems = [
        { label: 'Total Claims', value: claimsData.totalClaims },
        { label: 'Reversed Claims', value: claimsData.reversedClaims },
        { label: 'Net Claims', value: claimsData.netClaims },
        { label: 'Paid Claims', value: claimsData.paidClaims },
    ];

    // Rate items (only for specific users)
    const rateItems = [
        { label: 'Reversal Rate (%)', value: rates.reversalRate },
        { label: 'Compensability Rate (%)', value: rates.compensabilityRate },
    ];

    return (
        <div className="flex flex-col gap-4">
                        {/* Main Claims Grid */}
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                            {claimsItems.map((item, index) => (
                    <ClaimCard
                                    key={index}
                        label={item.label}
                        value={item.value}
                        isLoading={isLoading}
                        error={!!error}
                    />
                            ))}
                        </div>

                        {/* Rates Section (only for specific users) */}
                        {showRates && (
                            <div className="grid grid-cols-2 gap-4 pt-4 border-t border-border">
                                {rateItems.map((item, index) => (
                        <ClaimCard
                                        key={index}
                            label={item.label}
                            value={`${item.value}%`}
                            isLoading={isLoading}
                            error={!!error}
                        />
                                ))}
                            </div>
                        )}
        </div>
    );
}

export default Claim;
